import dedent from "dedent";
import * as Y from "yjs";

import { twColors } from "../utils/colors.generated";
import { nodeToLatex } from "../utils/nodeToLatex";
import { sanitizeLatex } from "../utils/sanitizeLatex";

import type { NodeJson } from "@corca/data";

export type ImageAttrs = { key: string; name: string; pngName?: string };

export type Externals = {
  commands: Set<string>;
  images: ImageAttrs[];
};

const FONT_SCALE_FACTOR = 10 / 16;

// const PX_TO_PT_FACTOR = 0.75;

const LINE_WIDTH_PX = 720;

const BASELINESKIP_PLACEHOLDER = "__BASELINESKIP__";
const BASELINESKIP_REGEX = new RegExp(BASELINESKIP_PLACEHOLDER, "g");
const BASELINESKIPS_BATCH_REGEX = new RegExp(
  `(${BASELINESKIP_PLACEHOLDER}(\\s*${BASELINESKIP_PLACEHOLDER})*)`,
  "g",
);

/** Matching the editor's 2rem `BLOCK_INDENT_STEP`. */
const INDENT_STEP_EM = 2;

const INDENTED_NODES = ["paragraph", "bulletlist", "orderedlist", "tasklist"];

function createColor(str: string | null | undefined): { name: string; dependencies: string[] } {
  if (!str) {
    return { name: "black", dependencies: [] };
  }

  const [color, shade] = str.split("-");
  const name = (shade ? `${color}_${shade}` : color) as keyof typeof twColors;
  const definition = `\\definecolor{${name}}{RGB}{${twColors[name] ?? "0, 0, 0"}}`;
  return { name, dependencies: [definition] };
}

const DEPENDENCIES = {
  strike: () => ["\\usepackage[normalem]{ulem}"],
  table: () => [
    "\\usepackage{xltabular,booktabs}",
    "\\usepackage{multirow}",
    "\\renewcommand{\\arraystretch}{1.5}",
    "\\renewcommand\\tabularxcolumn[1]{m{#1}}",
  ],
  codeBlock: () => [
    "\\usepackage{listings}",
    "\\usepackage{xcolor}",
    "\\definecolor{backcolor}{rgb}{0.95,0.95,0.92}",
    dedent(`
    \\lstdefinestyle{codestyle}{
      basicstyle=\\ttfamily\\footnotesize,
      numberstyle=\\ttfamily\\tiny,
      frame=single,
      backgroundcolor=\\color{backcolor},
      rulecolor=\\color{backcolor},
      numbers=left,
      numbersep=10pt,
      framesep=5pt,
      xleftmargin=20pt
    }`),
    "\\lstset{style=codestyle}",
  ],
  blockquote: () => [
    "\\usepackage[framemethod=TikZ]{mdframed}",
    dedent(`
    \\renewenvironment{quote}
    {\\small\\list{}{\\rightmargin=0pt \\leftmargin=10pt}%
     \\item\\relax}
    {\\endlist}`),
    dedent(`
    \\surroundwithmdframed[
      leftmargin=15pt,
      innerleftmargin=0pt,
      topline=false,
      bottomline=false,
      rightline=false,
      skipabove=\\topsep,
      skipbelow=\\topsep
    ]{quote}`),
  ],
};

function getColumnCount(row: Y.XmlElement) {
  let count = 0;
  row.forEach((child) => {
    if (child instanceof Y.XmlElement) {
      count += parseInt(child.getAttribute("colspan") ?? "1", 10);
    }
  });
  return count;
}

function firstElement(node: Y.XmlElement): Y.XmlElement | null {
  let child = node.firstChild;
  while (child && !(child instanceof Y.XmlElement)) {
    child = child?.nextSibling ?? null;
  }
  return child;
}

function getAttrs(node: Y.XmlElement) {
  const attrs = Object.entries(node.getAttributes()).reduce(
    (acc, [key, val]) => {
      acc[key.toLowerCase()] = val === "null" ? null : val;
      return acc;
    },
    {} as Record<string, string | null | undefined>,
  );
  return attrs;
}

/**
 * Scopes the paragraph's indent to a group.
 * The first-line indent is explicit space rather than `\parindent`: a heading drops the indent of
 * the paragraph after it. Centered and right-aligned text skips it: the space would start an empty
 * line before the alignment environment.
 */
function getIndentWrapper(attrs: ReturnType<typeof getAttrs>): [string, string] | null {
  const indent = Number(attrs.indent) || 0;
  const isAligned = attrs.alignment === "center" || attrs.alignment === "right";
  const firstLineIndent = !isAligned && String(attrs.firstlineindent) === "true";

  if (!indent && !firstLineIndent) {
    return null;
  }

  const lengths = [
    indent ? `\\setlength{\\leftskip}{${indent * INDENT_STEP_EM}em}` : "",
    firstLineIndent ? `\\hspace*{${INDENT_STEP_EM}em}\\ignorespaces` : "",
  ].join("");

  // `\leftskip` takes effect when the paragraph is broken into lines, so `\par` has to come
  // before the group closes.
  return [`{${lengths}`, "\\par}"];
}

function getSerializedResult(
  text: string,
  commands?: Set<string>,
  images?: ImageAttrs[],
): { text: string; externals?: Partial<Externals> } {
  if ((!commands || commands.size === 0) && (!images || images.length === 0)) {
    return { text };
  }

  return {
    text,
    externals: {
      commands: commands?.size ? new Set(commands) : undefined,
      images: images?.length ? images : undefined,
    },
  };
}

function serializeNodeAsLatex({
  node,
  parent,
  index,
  environments,
  getNodeJson,
}: {
  node: Y.XmlElement | Y.XmlText;
  parent: Y.XmlElement | Y.XmlFragment | null;
  index: number;
  environments: string[];
  getNodeJson: (nodeId: string) => NodeJson | null;
}): { text: string; externals?: Partial<Externals> } {
  const externalCommands = new Set<string>();
  const images: ImageAttrs[] = [];

  let wrapperCommands: (string | [string, string] | [string, string, "inline"])[] = [];
  let childEnvironments = environments;

  if (node instanceof Y.XmlText) {
    const [textContent, commands] = processYXmlText(
      node,
      "latex",
      environments.includes("lstlisting"),
    );
    commands.forEach((cmd) => externalCommands.add(cmd));
    return getSerializedResult(textContent, externalCommands, images);
  }

  const nodeName = node.nodeName.toLowerCase();
  const attrs = getAttrs(node);

  switch (nodeName) {
    case "image":
      if (attrs.key && attrs.name) {
        externalCommands.add("\\usepackage{graphicx}");
        externalCommands.add("\\graphicspath{{./images/}}");
        const path = `${attrs.key}--${attrs.name}`;

        // GIFs need to be converted to PNG for PDF generation
        const isGif = attrs.name.toLowerCase().endsWith(".gif");
        const pngName = isGif ? path.replace(/\.gif$/i, ".png") : undefined;
        const includePath = pngName ?? path;

        images.push({ key: attrs.key, name: path, pngName });

        const widthAttr = attrs.width ? parseInt(attrs.width, 10) : 300;
        const width = (widthAttr / LINE_WIDTH_PX).toFixed(4);

        return getSerializedResult(
          `\n\n\\includegraphics[width=${width}\\linewidth]{${includePath}}\n\n`,
          externalCommands,
          images,
        );
      }
      return getSerializedResult("", externalCommands, images);

    // TODO: convert SVG to TikZ here
    case "graph":
      return getSerializedResult("", externalCommands, images);

    case "fileattachment":
      return getSerializedResult("", externalCommands, images);

    case "hardbreak": {
      // @see https://www.overleaf.com/learn/latex/Errors/LaTeX_Error%3A_There%27s_no_line_here_to_end
      const isInAligningEnv = environments.some((env) =>
        ["center", "flushright", "flushleft"].includes(env),
      );
      let text = "\\\\\n";
      if (isInAligningEnv) {
        text = `${BASELINESKIP_PLACEHOLDER}\n`;
      }
      if (environments.includes("table")) {
        text = "\\newline ";
      }
      return getSerializedResult(text, externalCommands, images);
    }

    case "paragraph": {
      if (!environments.length) {
        const isEmpty = !node.toArray().some((child) => {
          if (child instanceof Y.XmlText) {
            return (child.toString() as string).trim().length > 0;
          }
          return child instanceof Y.XmlElement;
        });

        if (isEmpty) {
          return getSerializedResult("~", externalCommands, images);
        }
      }

      const parentChildCount = parent instanceof Y.XmlElement ? parent.length : 0;

      if (environments.includes("table") && index < parentChildCount - 1) {
        wrapperCommands.push(["", "\\newline", "inline"]);
      }
      break;
    }

    case "inlineexpression": {
      let text = "";
      const nodeId = attrs.nodeid;
      if (nodeId) {
        const nodeJson = getNodeJson(nodeId);
        if (nodeJson) {
          const latex = nodeToLatex(nodeJson);
          latex.packages.forEach((pkg) => externalCommands.add(pkg));
          text += wrapMathExpression(latex.text);
        }
      }
      return getSerializedResult(text, externalCommands, images);
    }

    case "table": {
      const firstRow = firstElement(node);
      if (firstRow) {
        const columnCount = getColumnCount(firstRow);
        const layout = Array.from({ length: columnCount }).fill(`X`);
        let i = 0;
        firstRow.forEach((cell) => {
          if (cell instanceof Y.XmlElement) {
            const colwidth = parseInt(cell.getAttribute("colwidth") ?? "0", 10);
            const colspan = parseInt(cell.getAttribute("colspan") ?? "1", 10);

            if (colwidth) {
              // if colspan > 1, split the fixed width evenly between columns
              for (let j = i; j < i + colspan; j++) {
                layout[j] = `m{${(colwidth / LINE_WIDTH_PX / colspan).toFixed(4)}\\textwidth}`;
              }
            }
            i += colspan;
          }
        });

        childEnvironments = [...childEnvironments, "table"];
        wrapperCommands.push([
          `\\begin{xltabular}{\\textwidth}{${layout.join("|")}}`,
          "\\end{xltabular}",
        ]);
        DEPENDENCIES.table().forEach((dep) => externalCommands.add(dep));
      }
      break;
    }

    case "tablerow": {
      const firstChild = firstElement(node);
      if (firstChild?.nodeName.toLowerCase() === "tableheader") {
        wrapperCommands.push(["", " \\\\\n\\specialrule{1pt}{0pt}{0pt}\n", "inline"]);
      } else {
        const line = buildTableLine(node);
        wrapperCommands.push(["", ` \\\\\n${line}\n`, "inline"]);
      }
      break;
    }

    case "tableheader":
    case "tablecell": {
      const columnCount = parent instanceof Y.XmlElement ? getColumnCount(parent) : 1;
      let start = "";
      let end = "";
      let alignment = "";
      const colspan = parseInt(attrs.colspan ?? "1", 10);
      const rowspan = parseInt(attrs.rowspan ?? "1", 10);

      const cellIndex =
        parent
          ?.toArray()
          .filter((n) => n instanceof Y.XmlElement)
          .indexOf(node) ?? 0;

      const isLastColumn = cellIndex + colspan < columnCount;

      if (nodeName === "tableheader") {
        start = `\\textbf{${start}`;
        end += "}";
      }

      if (rowspan > 1) {
        start = `\\multirow{${rowspan}}*{${start}`;
        end += "}";
      }

      if (attrs.alignment === "center") {
        alignment = isLastColumn ? "c|" : "c";
      } else if (attrs.alignment === "right") {
        alignment = isLastColumn ? "r|" : "r";
      }
      if (alignment || colspan > 1) {
        if (!alignment) {
          alignment = isLastColumn ? "l|" : "l";
        }
        start = `\\multicolumn{${colspan}}{${alignment}}{${start}`;
        end += "}";
      }

      if (cellIndex > 0) {
        start = ` & ${start}`;
      }

      wrapperCommands.push([start, end, "inline"]);
      break;
    }

    case "codeblock":
      childEnvironments = [...childEnvironments, "lstlisting"];
      wrapperCommands.push(["\\begin{lstlisting}", "\\end{lstlisting}"]);
      DEPENDENCIES.codeBlock().forEach((dep) => externalCommands.add(dep));
      break;

    case "blockquote":
      childEnvironments = [...childEnvironments, "quote"];
      wrapperCommands.push(["\\begin{quote}", "\\end{quote}"]);
      DEPENDENCIES.blockquote().forEach((dep) => externalCommands.add(dep));
      break;

    case "bulletlist":
      childEnvironments = [...childEnvironments, "itemize"];
      wrapperCommands.push(["\\begin{itemize}", "\\end{itemize}"]);
      break;

    case "orderedlist":
      childEnvironments = [...childEnvironments, "enumerate"];
      wrapperCommands.push(["\\begin{enumerate}", "\\end{enumerate}"]);
      break;

    case "listitem":
      wrapperCommands = ["\\item"];
      break;

    case "documenttitle":
      wrapperCommands = ["\\title"];
      break;

    case "heading":
      switch (parseInt(attrs.level ?? "1", 10)) {
        case 1:
          wrapperCommands = ["\\section*"];
          break;

        case 2:
          wrapperCommands = ["\\subsection*"];
          break;

        case 3:
          wrapperCommands = ["\\subsubsection*"];
          break;

        case 4:
          wrapperCommands = ["\\paragraph"];
          break;

        case 5:
          wrapperCommands = ["\\subparagraph"];
          break;

        case 6:
          wrapperCommands = ["\\subparagraph"];
          break;

        default:
          wrapperCommands = ["\\section*"];
          break;
      }
      break;

    case "horizontalrule":
      return getSerializedResult("\n\n\\hrulefill", externalCommands, images);

    case "callout": {
      // Extract color before dash (e.g., 'yellow' from 'yellow-100')
      const color = createColor(attrs.color);

      wrapperCommands = [
        `\\parbox{\\dimexpr\\linewidth-2\\fboxsep\\relax}`,
        `\n\n\\colorbox{${color.name}}`,
      ];
      childEnvironments = [...childEnvironments, "colorbox"];
      color.dependencies.forEach((dep) => externalCommands.add(dep));
      externalCommands.add("\\setlength{\\fboxsep}{10pt}");
      break;
    }

    // <Deprecated>
    case "italic":
      wrapperCommands.push("\\textit");
      break;
    case "bold":
      wrapperCommands.push("\\textbf");
      break;
    case "underline":
      wrapperCommands.push("\\underline");
      break;
    case "strike":
      wrapperCommands.push("\\sout");
      DEPENDENCIES.strike().forEach((dep) => externalCommands.add(dep));
      break;
    case "code":
      wrapperCommands.push(["\\verb|", "|", "inline"]);
      break;
    case "subscript":
      wrapperCommands.push("\\textsubscript");
      break;
    case "superscript":
      wrapperCommands.push("\\textsuperscript");
      break;
    case "textstyle":
      if (attrs.size && attrs.size !== "null") {
        const ptSize = parseInt(attrs.size, 10) * FONT_SCALE_FACTOR;
        wrapperCommands.push([`\\fontsize{${ptSize}pt}{${ptSize}pt}\\selectfont`, "\\normalsize"]);
      }
      if (attrs.color && attrs.color !== "null") {
        const color = createColor(attrs.color);
        wrapperCommands.push(`\\textcolor{${color.name}}`);
        color.dependencies.forEach((dep) => externalCommands.add(dep));
      }
      break;
    // </Deprecated>
  }

  // Handle alignment for non-table elements
  if (nodeName !== "tableheader" && nodeName !== "tablecell") {
    const parentIsCell =
      parent instanceof Y.XmlElement &&
      (parent.nodeName.toLowerCase() === "tableheader" ||
        parent.nodeName.toLowerCase() === "tablecell");
    let { alignment } = attrs as { alignment: string | undefined };
    // ignore redundant alignment in cells
    if (parentIsCell && parent.getAttribute("alignment") === alignment) {
      alignment = undefined;
    }
    if (alignment === "center") {
      childEnvironments = [...childEnvironments, "center"];
      wrapperCommands.push(["\\begin{center}", "\\end{center}"]);
    } else if (alignment === "right") {
      childEnvironments = [...childEnvironments, "flushright"];
      wrapperCommands.push(["\\begin{flushright}", "\\end{flushright}"]);
    }
  }

  // Outside the alignment environment: `\centering` and `\raggedleft` set `\leftskip` themselves,
  // and overwriting it inside them would drop the alignment. A list's items keep their own margin
  // on top of it.
  if (INDENTED_NODES.includes(nodeName) && !environments.includes("table")) {
    const indentWrapper = getIndentWrapper(attrs);

    if (indentWrapper) {
      wrapperCommands.push(indentWrapper);
    }
  }

  if (nodeName === "table") {
    fillTableWithPlaceholderCells(node);
  }

  const res = serializeTextSliceRecursive({
    content: node,
    context: "latex",
    parent: node,
    environments: childEnvironments,
    getNodeJson,
  });
  res.externals?.images?.forEach((img) => {
    if (!images.find((i) => i.name === img.name && i.key === img.key)) {
      images.push(img);
    }
  });

  let nodeContentStr = res.text;

  res.externals?.commands?.forEach((dep) => {
    externalCommands.add(dep);
  });

  for (const wrapperCommand of wrapperCommands) {
    if (Array.isArray(wrapperCommand)) {
      const [open, close, type] = wrapperCommand;
      if (type === "inline") {
        nodeContentStr = [open, nodeContentStr.replace(/\n/g, " "), close].join(" ");
      } else {
        nodeContentStr = ["", open, nodeContentStr, close, ""].join("\n");
      }
    } else {
      nodeContentStr = `${wrapperCommand}{${nodeContentStr}}`;
    }
  }

  return getSerializedResult(nodeContentStr, externalCommands, images);
}

export function getTextContent(node: Y.XmlElement): string {
  let text = "";
  node.forEach((child) => {
    if (child instanceof Y.XmlText) {
      const [content] = processYXmlText(child, "plain");
      text += content;
    } else if (child instanceof Y.XmlElement) {
      text += getTextContent(child);
    }
  });
  return text;
}

function wrapMathExpression(text: string): string {
  // always use inline math delimiters for math expressions
  // displaystyle is enabled for all math expressions by default
  return `\\( ${text} \\)`;
}

type DeltaOp = { insert: string; attributes?: { [key: string]: unknown } };

function processYXmlText(
  textNode: Y.XmlText,
  context: "latex" | "plain",
  skipSanitize = false,
): [string, Set<string>] {
  let str = "";
  const commands = new Set<string>();
  const delta = textNode.toDelta() as DeltaOp[];
  for (const op of delta) {
    let insertText = op.insert;
    if (context === "latex") {
      if (!skipSanitize) {
        insertText = sanitizeLatex(insertText, { keepTabs: Boolean(op.attributes?.code) });
      }

      if (op.attributes?.bold) {
        insertText = `\\textbf{${insertText}}`;
      }
      if (op.attributes?.italic) {
        insertText = `\\textit{${insertText}}`;
      }
      if (op.attributes?.underline) {
        insertText = `\\underline{${insertText}}`;
      }
      if (op.attributes?.strike) {
        insertText = `\\sout{${insertText}}`;
        DEPENDENCIES.strike().forEach((dep) => commands.add(dep));
      }
      if (op.attributes?.code) {
        insertText = `\\verb|${insertText}|`;
      }
      if (op.attributes?.textStyle && typeof op.attributes.textStyle === "object") {
        const textStyle = op.attributes.textStyle as {
          size?: string | null;
          color?: string | null;
        };
        if (textStyle.size) {
          const ptSize = parseInt(textStyle.size, 10) * FONT_SCALE_FACTOR;
          insertText = `\\fontsize{${ptSize}pt}{${ptSize}pt}\\selectfont ${insertText} \\normalsize`;
        }
        if (textStyle.color) {
          const color = createColor(textStyle.color);
          insertText = `\\textcolor{${color.name}}{${insertText}}`;
          color.dependencies.forEach((dep) => commands.add(dep));
        }
      }
    }
    str += insertText;
  }

  return [str, commands];
}

type SerializeNode = (
  node: Y.XmlElement,
  context: "plain" | "latex",
  index: number,
) => { text: string; externals?: Partial<Externals> } | undefined;

function serializeTextSliceRecursive({
  content,
  context,
  parent,
  environments = [],
  getNodeJson,
  skip = 0,
  serializeNode,
}: {
  content: Y.XmlFragment | Y.XmlElement;
  context: "plain" | "latex";
  parent?: Y.XmlElement | Y.XmlFragment;
  environments?: string[];
  getNodeJson: (nodeId: string) => NodeJson | null;
  skip?: number;
  serializeNode?: SerializeNode;
}): { text: string; externals?: Partial<Externals> } {
  const externalCommands = new Set<string>();
  const images: ImageAttrs[] = [];

  let text = "";
  let first = true;
  const blockSeparator = "\n\n";

  content.forEach((node, index) => {
    if (index < skip) {
      return;
    }
    let nodeText = "";

    const customSerialized =
      node instanceof Y.XmlElement ? serializeNode?.(node, context, index) : undefined;
    if (customSerialized) {
      nodeText += customSerialized.text;
      customSerialized.externals?.commands?.forEach((dep) => externalCommands.add(dep));
      customSerialized.externals?.images?.forEach((img) => images.push(img));
    } else if (context === "latex") {
      const serialized = serializeNodeAsLatex({
        node,
        parent: parent ?? null,
        index,
        environments,
        getNodeJson,
      });
      nodeText += serialized.text;
      serialized.externals?.commands?.forEach((pkg) => externalCommands.add(pkg));
      serialized.externals?.images?.forEach((img) => {
        if (!images.find((i) => i.name === img.name && i.key === img.key)) images.push(img);
      });
    } else if (node instanceof Y.XmlText) {
      const [textSlice, commands] = processYXmlText(
        node,
        context,
        environments.includes("lstlisting"),
      );
      commands.forEach((cmd) => externalCommands.add(cmd));
      nodeText += textSlice;
    } else if (node instanceof Y.XmlElement) {
      const nodeId = getAttrs(node).nodeid;
      if (nodeId) {
        const nodeJson = getNodeJson(nodeId);
        if (nodeJson) {
          const latex = nodeToLatex(nodeJson);
          nodeText += wrapMathExpression(latex.text);
          latex.packages.forEach((dep) => externalCommands.add(dep));
        }
      } else {
        const childRes = serializeTextSliceRecursive({
          content: node,
          context,
          parent: node,
          environments,
          getNodeJson,
          skip: 0,
          serializeNode,
        });
        nodeText += childRes.text;
        childRes.externals?.commands?.forEach((dep) => externalCommands.add(dep));
        childRes.externals?.images?.forEach((img) => {
          if (!images.find((i) => i.name === img.name && i.key === img.key)) images.push(img);
        });
      }
    }

    const isBlock = node instanceof Y.XmlElement && isBlockElement(node.nodeName.toLowerCase());
    const hasContent = nodeText.trim().length > 0;

    if (isBlock && hasContent && blockSeparator) {
      if (first) {
        first = false;
      } else {
        text += blockSeparator;
      }
    }
    text += nodeText;
  });

  text = text
    .replace(/\n{2,}/g, "\n\n")
    // group multiple line breaks in one baselineskip command
    .replace(BASELINESKIPS_BATCH_REGEX, (match) => {
      const count = match.match(BASELINESKIP_REGEX)?.length ?? 1;
      return `\\\\[${count - 1}\\baselineskip]`;
    })
    // add empty box before "\\" if line is empty to prevent "There's no line here to end" error and underfull warning
    .replace(/(^|\n)\\\\($|\n)/g, (match, p1, p2) => `${p1}\\mbox{}\\\\${p2}`);

  return getSerializedResult(text, externalCommands, images);
}

export function serializeTextSlice({
  content,
  context,
  getNodeJson,
  skip = 0,
  serializeNode,
}: {
  content: Y.XmlFragment | Y.XmlElement;
  context: "plain" | "latex";
  getNodeJson: (nodeId: string) => NodeJson | null;
  skip?: number;
  serializeNode?: SerializeNode;
}): { text: string; externals: Externals } {
  const { text, externals } = serializeTextSliceRecursive({
    content,
    context,
    getNodeJson,
    skip,
    serializeNode,
  });

  return {
    text,
    externals: {
      commands: externals?.commands ?? new Set<string>(),
      images: externals?.images ?? [],
    },
  };
}

function isBlockElement(nodeName: string): boolean {
  return [
    "paragraph",
    "heading",
    "blockquote",
    "codeblock",
    "bulletlist",
    "orderedlist",
    "listitem",
    "table",
    "tablerow",
    "horizontalrule",
    "documenttitle",
  ].includes(nodeName);
}

export function fillTableWithPlaceholderCells(table: Y.XmlElement): void {
  const rows = table.toArray().filter((node) => node instanceof Y.XmlElement);
  const rowCount = rows.length;
  const colCount = getColumnCount(rows[0]);

  let hasSpans = false;

  const spans: number[][] = Array.from({ length: rowCount }, () => Array<number>(colCount).fill(0));

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    const row = rows[rowIndex];
    const cells = row.toArray().filter((node) => node instanceof Y.XmlElement);

    const newChildren: Y.XmlElement[] = [];
    let cellIndex = 0;

    for (let i = 0; i < colCount; i++) {
      const pendingSpan = spans[rowIndex][i];
      const noline = !!spans[rowIndex + 1]?.[i];

      if (pendingSpan) {
        // insert placeholder
        const placeholder = new Y.XmlElement("tablecell");
        placeholder.setAttribute("colspan", String(pendingSpan));
        if (noline) placeholder.setAttribute("rowspan", "-1");
        const emptyParagraph = new Y.XmlElement("paragraph");
        placeholder.insert(0, [emptyParagraph]);
        newChildren.push(placeholder);
        i += pendingSpan - 1;

        continue;
      }

      const cell = cells[cellIndex];
      cellIndex += 1;
      if (!cell) break;

      const colspan = parseInt(cell.getAttribute("colspan") || "1", 10);
      const rowspan = parseInt(cell.getAttribute("rowspan") || "1", 10);

      newChildren.push(cell.clone());

      if (rowspan > 1) {
        for (let r = rowIndex + 1; r < rowIndex + rowspan; r++) {
          if (spans[r]) spans[r][i] = colspan;
          hasSpans = true;
        }
      }

      if (colspan > 1) {
        i += colspan - 1;
      }
    }

    if (hasSpans) {
      row.delete(0, row.length);
      row.push(newChildren);
    }
  }
}

function buildTableLine(content: Y.XmlElement): string {
  let index = 1;
  const ranges: [number, number][] = [];
  let range: [number, number] | null = null;

  const cells: Y.XmlElement[] = [];
  content.forEach((child) => {
    if (
      child instanceof Y.XmlElement &&
      (child.nodeName.toLowerCase() === "tablecell" ||
        child.nodeName.toLowerCase() === "tableheader")
    ) {
      cells.push(child);
    }
  });

  cells.forEach((cell) => {
    const colspan = parseInt(cell.getAttribute("colspan") ?? "1", 10);
    const rowspan = parseInt(cell.getAttribute("rowspan") ?? "1", 10);
    const noline = rowspan > 1 || rowspan === -1;

    if (noline) {
      if (range) {
        ranges.push(range);
        range = null;
      }
    } else if (!range) {
      range = [index, index];
    } else {
      range[1] = index;
    }

    if (colspan > 1) {
      index += colspan - 1;
    }
    index += 1;
  });

  if (range && (range[0] > 1 || range[1] < index)) {
    ranges.push(range);
  }

  if (ranges.length > 0) {
    return ranges.map(([start, end]) => `\\cline{${start}-${end}}`).join(" ");
  }

  return "\\hline";
}
